/**
 * Report allowance pricing. Client-safe: no server imports.
 *
 * One "report credit" covers a single property report of any kind — instant
 * address report, enhanced/GIS report, Map Exhibit, or Title Policy Analyzer
 * run. A report is charged once, when it is first created; every later
 * re-render, export, share link and co-brand change on that report is free.
 */

export type PlanTier = "free" | "starter" | "pro" | "team";

export interface PlanDefinition {
  tier: PlanTier;
  name: string;
  /** Human-readable Stripe price ID for the subscription, if any. */
  priceId: string | null;
  /** Monthly subscription cost in cents. */
  monthlyCents: number;
  /** Reports included in each billing month. */
  included: number;
  /** Price of each additional report, in cents. */
  overageCents: number;
  /** Human-readable Stripe price ID used to buy one extra report. */
  overagePriceId: string;
  /** Distinct logins that may use the account. */
  seats: number;
  /** Free directory ad credits granted each month. */
  directoryAdCredits: number;
  /** Whether support requests are routed to the priority queue. */
  prioritySupport: boolean;
  /** Quantity presets offered when buying extra reports in bulk. */
  bulkOverageQuantities: number[];
  blurb: string;
  features: string[];
}

export const PLANS: Record<PlanTier, PlanDefinition> = {
  free: {
    tier: "free",
    name: "Free",
    priceId: null,
    monthlyCents: 0,
    included: 1,
    overageCents: 500,
    overagePriceId: "report_single_500",
    seats: 1,
    directoryAdCredits: 0,
    prioritySupport: false,
    bulkOverageQuantities: [1, 3, 5, 10],
    blurb: "One free Title Policy Analyzer run each month. Every extra report is a single $5 purchase. No subscription, no expiring credits.",
    features: [
      "Unlimited address search, lot picker and map browsing",
      "1 free Title Policy Analyzer run per month",
      "$5 per additional report",
      "No subscription",
      "Purchased reports never expire",
      "Co-branding included on every report you run",
      "1 login",
    ],
  },
  starter: {
    tier: "starter",
    name: "Starter",
    priceId: "plan_starter_monthly",
    monthlyCents: 6000,
    included: 20,
    overageCents: 400,
    overagePriceId: "report_single_400",
    seats: 1,
    directoryAdCredits: 0,
    prioritySupport: false,
    bulkOverageQuantities: [1, 5, 10],
    blurb: "For agents running a handful of due-diligence checks each month.",
    features: [
      "20 reports per month",
      "$4 per additional report",
      "Partner co-branding included",
      "1 login",
    ],
  },
  pro: {
    tier: "pro",
    name: "Pro",
    priceId: "plan_pro_monthly",
    monthlyCents: 10000,
    included: 50,
    overageCents: 300,
    overagePriceId: "report_single_300",
    seats: 3,
    directoryAdCredits: 500,
    prioritySupport: true,
    bulkOverageQuantities: [1, 10, 25],
    blurb: "For busy brokerages and title shops.",
    features: [
      "50 reports per month",
      "$3 per additional report",
      "Partner co-branding included",
      "3 logins",
      "500 free directory ad credits each month",
      "Priority support",
    ],
  },
  team: {
    tier: "team",
    name: "Team",
    priceId: "plan_team_monthly",
    monthlyCents: 25000,
    included: 250,
    overageCents: 200,
    overagePriceId: "report_single_200",
    seats: 6,
    directoryAdCredits: 1000,
    prioritySupport: true,
    bulkOverageQuantities: [1, 10, 25, 50],
    blurb: "High-volume research for teams and lenders.",
    features: [
      "250 reports per month",
      "$2 per additional report",
      "Partner co-branding included",
      "6 logins",
      "1,000 free directory ad credits each month",
      "Priority support",
    ],
  },
};

/**
 * Subscription plans offered to real estate professionals. Homeowners stay on
 * the free tier plus one-time report credits; these tiers add branded reports,
 * extra logins, higher daily operation ceilings and directory ad credits.
 */
export const PAID_PLANS: PlanDefinition[] = [PLANS.starter, PLANS.pro, PLANS.team];


/** Maps a subscription price ID back to the plan it grants. */
export function planForPriceId(priceId: string | null | undefined): PlanDefinition {
  if (!priceId) return PLANS.free;
  const match = PAID_PLANS.find((p) => p.priceId === priceId);
  return match ?? PLANS.free;
}

/** Every one-time price the app may charge for a single extra report. */
export const OVERAGE_PRICE_IDS = [
  "report_single_500",
  "report_single_400",
  "report_single_300",
  "report_single_200",
] as const;

export function isOveragePriceId(value: string): boolean {
  return (OVERAGE_PRICE_IDS as readonly string[]).includes(value);
}

export function formatUsd(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** Calendar-month bucket used to reset the free allowance. */
export function periodKey(date: Date = new Date()): string {
  const y = date.getUTCFullYear();
  const m = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  return `${y}-${m}`;
}

/** First day of the next calendar month, in UTC — when the free allowance resets. */
export function nextResetDate(date: Date = new Date()): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
}

/** Adds whole months to a UTC date, clamping the day to the target month. */
function addMonthsUtc(date: Date, months: number): Date {
  const day = date.getUTCDate();
  const target = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth() + months,
      1,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target;
}

/**
 * The allowance window a moment falls in.
 *
 * Subscribers track their Stripe billing cycle exactly: the window opens on
 * the subscription anchor (`current_period_start`) and rolls over on each
 * monthly anniversary, so included reports reset when Stripe bills rather than
 * on the first of the calendar month. Accounts with no anchor (free tier) keep
 * the calendar-month window.
 */
export interface BillingCycle {
  /** Bucket written to `report_credits.period_key`. */
  key: string;
  startsAt: string;
  /** When the included allowance rolls over — the renewal date for subscribers. */
  resetsAt: string;
  /** True when the window follows a Stripe subscription anchor. */
  anchored: boolean;
}

export function billingCycle(
  anchorIso: string | null | undefined,
  now: Date = new Date(),
): BillingCycle {
  const anchor = anchorIso ? new Date(anchorIso) : null;
  if (!anchor || Number.isNaN(anchor.getTime()) || anchor.getTime() > now.getTime()) {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return {
      key: periodKey(now),
      startsAt: start.toISOString(),
      resetsAt: nextResetDate(now).toISOString(),
      anchored: false,
    };
  }

  // Jump most of the way in whole months, then step to the window holding now.
  const monthsApart =
    (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - anchor.getUTCMonth());
  let start = addMonthsUtc(anchor, Math.max(0, monthsApart - 1));
  for (let i = 0; i < 24; i++) {
    const next = addMonthsUtc(start, 1);
    if (next.getTime() > now.getTime()) break;
    start = next;
  }
  return {
    key: `cycle-${start.toISOString().slice(0, 10)}`,
    startsAt: start.toISOString(),
    resetsAt: addMonthsUtc(start, 1).toISOString(),
    anchored: true,
  };
}

/**
 * Prepaid report credits never expire. This helper is kept so legacy rows that
 * still carry an `expires_at` timestamp can be formatted, but nothing new is
 * ever stamped with an expiry.
 */

/** Human-readable last day the credits can be used, e.g. "Aug 31". */
export function formatCreditExpiry(expiresAtIso: string | null | undefined): string | null {
  if (!expiresAtIso) return null;
  const expiry = new Date(expiresAtIso);
  if (Number.isNaN(expiry.getTime())) return null;
  const lastDay = new Date(expiry.getTime() - 24 * 60 * 60 * 1000);
  return lastDay.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export const CREDIT_EXPIRY_NOTICE =
  "Purchased reports never expire. Your free monthly report resets on the first of each month.";

export interface EntitlementSummary {
  tier: PlanTier;
  planName: string;
  included: number;
  used: number;
  remaining: number;
  /** Prepaid report credits the user already bought. They never expire. */
  prepaid: number;
  /** Legacy expiry timestamp, if a pre-2026 credit row still carries one. */
  prepaidExpiresAt: string | null;
  nextReportCents: number;
  nextReportPriceId: string;
  seats: number;
  directoryAdCredits: number;
  prioritySupport: boolean;
  periodKey: string;
  /** When the included allowance rolls over. */
  resetsAt: string;
  /** Start of the current allowance window. */
  cycleStartsAt: string;
  /** True when the window follows the Stripe billing cycle rather than the calendar month. */
  cycleAnchored: boolean;
  /** When Stripe next bills the subscription; null on the free tier. */
  renewsAt: string | null;
  /** True when the subscription is set to end at `renewsAt` instead of renewing. */
  cancelAtPeriodEnd: boolean;
}

export function summarize(
  plan: PlanDefinition,
  used: number,
  prepaid: number,
  now: Date = new Date(),
  prepaidExpiresAt: string | null = null,
  cycle: BillingCycle = billingCycle(null, now),
  cancelAtPeriodEnd = false,
  /**
   * Stripe's own `current_period_end` for the live subscription. It is the
   * authoritative renewal date — the computed anniversary is only a fallback
   * for rows that predate period tracking.
   */
  periodEnd: string | null = null,
): EntitlementSummary {
  const remaining = Math.max(plan.included - used, 0);
  return {
    tier: plan.tier,
    planName: plan.name,
    included: plan.included,
    used,
    remaining,
    prepaid,
    // Purchased credits never expire; only legacy rows may carry a timestamp.
    prepaidExpiresAt: prepaid > 0 ? prepaidExpiresAt : null,
    nextReportCents: plan.overageCents,
    nextReportPriceId: plan.overagePriceId,
    seats: plan.seats,
    directoryAdCredits: plan.directoryAdCredits,
    prioritySupport: plan.prioritySupport,
    periodKey: cycle.key,
    resetsAt: cycle.resetsAt,
    cycleStartsAt: cycle.startsAt,
    cycleAnchored: cycle.anchored,
    renewsAt: plan.tier === "free" ? null : (periodEnd ?? cycle.resetsAt),
    cancelAtPeriodEnd,
  };
}

/** Human-readable cycle date, e.g. "Sep 17, 2026". */
export function formatCycleDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
